import type { AnalyticsAttribution, AnalyticsSource } from '@omaplus/shared-types';

const MEDIUM_SOURCES = new Map<string, AnalyticsSource>([
  ['direct', 'direct'],
  ['email', 'email'],
  ['search', 'search'],
  ['organic', 'search'],
  ['social', 'social'],
  ['paid', 'paid'],
  ['cpc', 'paid'],
  ['ppc', 'paid'],
  ['referral', 'referral'],
]);

const SEARCH_HOSTS = new Set([
  'google.com',
  'google.fi',
  'bing.com',
  'duckduckgo.com',
  'yahoo.com',
  'ecosia.org',
]);
const SOCIAL_HOSTS = new Set([
  'facebook.com',
  'instagram.com',
  'linkedin.com',
  'x.com',
  'twitter.com',
  't.co',
  'youtube.com',
  'reddit.com',
  'tiktok.com',
  'threads.net',
]);
const CAMPAIGN_TOKEN = /^[a-z0-9][a-z0-9_-]{0,99}$/;

/**
 * The reserved same-origin query key. The successful member-login redirect
 * carries it only as a valueless login-return marker: it holds no attribution,
 * identity, or other value. Any occurrence, including a legacy valued handoff,
 * marks a document whose entry evidence is indeterminate.
 */
export const ANALYTICS_ENTRY_HANDOFF_PARAM = '__analytics_entry';

function isReservedEntryKey(encodedKey: string): boolean {
  try {
    return decodeURIComponent(encodedKey.replace(/\+/g, ' ')) === ANALYTICS_ENTRY_HANDOFF_PARAM;
  } catch {
    // An undecodable key cannot be the reserved key; keep it untouched.
    return false;
  }
}

/**
 * Appends exactly one bare login-return marker to an already sanitized
 * same-origin path. Existing query parameters and the fragment are preserved
 * verbatim; any earlier occurrence of the reserved key, valued or not, is
 * dropped so a caller-supplied value can never ride along.
 */
export function withLoginReturnMarker(target: string): string {
  if (!target.startsWith('/') || target.startsWith('//') || target.startsWith('/\\')) {
    throw new Error('analytics login-return marker: target must be a same-origin path');
  }
  const hashIndex = target.indexOf('#');
  const beforeHash = hashIndex === -1 ? target : target.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : target.slice(hashIndex);
  const queryIndex = beforeHash.indexOf('?');
  const path = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : beforeHash.slice(queryIndex + 1);
  const kept = query.split('&').filter((part) => {
    if (part.length === 0) return false;
    const separator = part.indexOf('=');
    return !isReservedEntryKey(separator === -1 ? part : part.slice(0, separator));
  });
  return `${path}?${[...kept, ANALYTICS_ENTRY_HANDOFF_PARAM].join('&')}${hash}`;
}

/**
 * Whether a document URL's search string carries the reserved login-return key.
 * Such a document arrived from the identity provider, so its referrer and query
 * are not entry evidence; the caller must report it as unknown.
 */
export function isLoginReturnSearch(search: string): boolean {
  return new URLSearchParams(search).has(ANALYTICS_ENTRY_HANDOFF_PARAM);
}

/**
 * Query parameters that are entry-attribution evidence. They must never cross
 * an unauthenticated sign-in boundary: Analytics may inspect a member's current
 * URL only after that member has accepted collection.
 */
const ENTRY_ATTRIBUTION_QUERY_PARAMETERS = [
  ANALYTICS_ENTRY_HANDOFF_PARAM,
  'utm_source',
  'utm_medium',
  'utm_campaign',
] as const;

/**
 * Removes every entry-attribution query parameter while preserving unrelated
 * parameters. This deliberately does not read, classify, or return removed
 * values, and does not alter a URL fragment.
 */
export function withoutEntryAttributionQueryParameters(search: string): string {
  const parameters = new URLSearchParams(search);
  for (const parameter of ENTRY_ATTRIBUTION_QUERY_PARAMETERS) {
    parameters.delete(parameter);
  }
  const remaining = parameters.toString();
  return remaining === '' ? '' : `?${remaining}`;
}

/** Raw entry evidence available to either the browser or an initial API login request. */
export interface EntryAttributionInput {
  readonly referrer?: string;
  readonly origin?: string;
  /** The initial request search string, before router navigation can alter it. */
  readonly search?: string;
}

/** Bounded source category plus the four persisted attribution dimensions. */
export type EntryAttribution = Readonly<{ readonly source: AnalyticsSource }> &
  AnalyticsAttribution;

type CampaignToken =
  | Readonly<{ readonly kind: 'absent' | 'rejected' | 'unavailable' }>
  | Readonly<{ readonly kind: 'accepted'; readonly value: string }>;

type Referrer =
  | Readonly<{ readonly kind: 'absent' | 'internal' | 'rejected' | 'unavailable' }>
  | Readonly<{ readonly kind: 'external'; readonly host: string }>;

function hostMatches(hostname: string, knownHosts: ReadonlySet<string>): boolean {
  for (const knownHost of knownHosts) {
    if (hostname === knownHost || hostname.endsWith(`.${knownHost}`)) return true;
  }
  return false;
}

function classifyExternalHost(hostname: string): AnalyticsSource {
  if (hostMatches(hostname, SEARCH_HOSTS)) return 'search';
  if (hostMatches(hostname, SOCIAL_HOSTS)) return 'social';
  return 'referral';
}

function canonicalHostname(url: URL): string {
  return url.hostname.toLowerCase().replace(/\.$/, '');
}

function sameCanonicalOrigin(left: URL, right: URL): boolean {
  return (
    left.protocol === right.protocol &&
    left.port === right.port &&
    canonicalHostname(left) === canonicalHostname(right)
  );
}

function isIpv4Address(labels: readonly string[]): boolean {
  return (
    labels.length === 4 &&
    labels.every((label) => /^\d+$/.test(label) && Number(label) >= 0 && Number(label) <= 255)
  );
}

/** Validates the normalized hostname boundary the classifier accepts as a referrer host. */
export function isAnalyticsReferrerHost(value: string): boolean {
  const hostname = value.toLowerCase();
  if (
    hostname !== value ||
    hostname.length === 0 ||
    hostname.length > 253 ||
    hostname.endsWith('.localhost')
  ) {
    return false;
  }
  const labels = hostname.split('.');
  return (
    labels.length > 1 &&
    !isIpv4Address(labels) &&
    /^[a-z]/.test(labels.at(-1) ?? '') &&
    labels.every(
      (label) =>
        label.length > 0 && label.length <= 63 && /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(label),
    )
  );
}

function parseReferrer({ referrer, origin }: EntryAttributionInput): Referrer {
  if (referrer === undefined) return { kind: 'unavailable' };
  if (referrer.length === 0) return { kind: 'absent' };
  try {
    const referrerUrl = new URL(referrer);
    if (
      (referrerUrl.protocol !== 'http:' && referrerUrl.protocol !== 'https:') ||
      referrerUrl.username.length > 0 ||
      referrerUrl.password.length > 0
    ) {
      return { kind: 'rejected' };
    }
    if (origin !== undefined && sameCanonicalOrigin(referrerUrl, new URL(origin))) {
      return { kind: 'internal' };
    }
    const hostname = canonicalHostname(referrerUrl);
    return isAnalyticsReferrerHost(hostname)
      ? { kind: 'external', host: hostname }
      : { kind: 'rejected' };
  } catch {
    return { kind: 'rejected' };
  }
}

function decodeQueryPart(value: string): string | null {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return null;
  }
}

function parseCampaignToken(
  search: string | undefined,
  key: 'utm_source' | 'utm_medium' | 'utm_campaign',
): CampaignToken {
  if (search === undefined) return { kind: 'unavailable' };
  if (search.length === 0) return { kind: 'absent' };
  const query = search.startsWith('?') ? search.slice(1) : search;
  let result: CampaignToken = { kind: 'absent' };
  for (const part of query.split('&')) {
    const separator = part.indexOf('=');
    const encodedKey = separator === -1 ? part : part.slice(0, separator);
    if (decodeQueryPart(encodedKey) !== key) continue;
    if (result.kind !== 'absent') return { kind: 'rejected' };
    const encodedValue = separator === -1 ? '' : part.slice(separator + 1);
    const decodedValue = decodeQueryPart(encodedValue);
    if (decodedValue === null) return { kind: 'rejected' };
    const value = decodedValue.toLowerCase();
    result = CAMPAIGN_TOKEN.test(value) ? { kind: 'accepted', value } : { kind: 'rejected' };
  }
  return result;
}

function campaignValue(token: CampaignToken): string | null {
  return token.kind === 'accepted' ? token.value : null;
}

function noCampaignParameters(tokens: readonly CampaignToken[]): boolean {
  return tokens.every((token) => token.kind === 'absent');
}

/** Produces the one bounded entry claim; raw referrers and query values do not escape this call. */
export function classifyEntryAttribution(input: EntryAttributionInput): EntryAttribution {
  const referrer = parseReferrer(input);
  const campaignSource = parseCampaignToken(input.search, 'utm_source');
  const campaignMedium = parseCampaignToken(input.search, 'utm_medium');
  const campaignName = parseCampaignToken(input.search, 'utm_campaign');
  const recognizedMedium =
    campaignMedium.kind === 'accepted' ? MEDIUM_SOURCES.get(campaignMedium.value) : undefined;
  const source =
    recognizedMedium ??
    (referrer.kind === 'external'
      ? classifyExternalHost(referrer.host)
      : referrer.kind === 'absent' &&
          noCampaignParameters([campaignSource, campaignMedium, campaignName])
        ? 'direct'
        : 'unknown');

  return {
    source,
    referrerHost: referrer.kind === 'external' ? referrer.host : null,
    campaignSource: campaignValue(campaignSource),
    campaignMedium: campaignValue(campaignMedium),
    campaignName: campaignValue(campaignName),
  };
}
